import { LinkMemberSet } from "./LinkMemberSet";
import { Device } from "./Device";
import { IfTable, IfEntry } from "../InfoSets/IfTable";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { LinkMember } from "./LinkMember";
import { IpAddress, IpVersion } from "./IpAddress";
import { MacAddress } from "./MacAddress";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";

export enum LinkType {
    Unknown = 0,
    Manual,
    CDP,
    LLDP,
    STP,
    LAG,
    ExactTwoWay,
    ExactOneWay,
    EndNode,
    IndirectISL,
    WirelessEndNode,
    IpRoute,
    ARP,
    Virtual,
    VirtualMachine,
    Historical,
    VirtualHost,
    STPBlocking,
    VPN,
    VeloCloud,
    DeviceGroup,
    ISIS,
    HA
};

//export interface LinkMemberEntry {
//    link_member: LinkMemberJson;
//}

//export interface LinkJson {
//    link_type: string;
//    link_members: LinkMemberEntry[];
//}

export class Link implements ObjectSerializer {

    public LinkType: LinkType

    public LinkMembers: LinkMemberSet;
    public get HasLinkMembers(): boolean {
        return this.LinkMembers && this.LinkMembers.Count > 0;
    }

    public get LinkMemberCount(): number {
        return this.LinkMembers ? this.LinkMemberCount : 0;
    }

    public get NetworkMemberCount(): number {
        return this.LinkMembers ? this.LinkMembers.NetworkMemberCount : 0;
    }

    public get DisplayName(): string {
        var memberNames = new Array<string>(this.LinkMembers.Count);

        this.LinkMembers.ForEach((linkMember, i) => {
            memberNames[i] = linkMember.DisplayName;
        });

        memberNames.sort();

        var result = "";

        memberNames.forEach((name, index) => {
            if (result.length > 0)
                result += " <=> ";

            result += memberNames[index];
        });

        return result;
    }

    constructor(linkType?: LinkType) {
        this.LinkType = linkType;
        this.LinkMembers = new LinkMemberSet(this);
    }

    public AddMember(device: Device, ifEntry: IfEntry = null, linkType: LinkType = LinkType.Unknown): LinkMember {
        var newMember: LinkMember = this.LinkMembers.AddDeviceLinks(device, ifEntry);
        if ((newMember != null) && (linkType != LinkType.Unknown)) {
            newMember.LinkType = linkType;
        }
        return (newMember);
    }

    public AddMembers(linkMembers: LinkMemberSet): void {
        if (!linkMembers || linkMembers.Count <= 0)
            return;

        linkMembers.ForEach((linkMember) => {
            //if (!this.LinkMembers.Contains(linkMember))
                this.LinkMembers.Add(linkMember);
        });
    }

    public Clear(): void {
        this.LinkType = LinkType.Unknown;
        this.LinkMembers.Clear();
    }

    public Copy(): Link {
        var linkCopy: Link = null;

        //var linkJson: string = JSON.stringify(this);
        //linkCopy = <Link> JSON.parse(linkJson);

        return linkCopy;
    }

    public GetMember(device: Device, ifEntry: IfEntry = null): LinkMember {
        if (this.LinkMembers.Count <= 0)
            return null;

        return this.LinkMembers.Find(device, ifEntry);
    }

    public GetMemberIfEntry(device: Device): IfEntry {
        if (device == null)
            return null;
        if (this.LinkMembers.Count <= 0)
            return null;

        var foundIfEntry: IfEntry = this.LinkMembers.GetUniqueDeviceIfEntry(device);
        return foundIfEntry;
    }

    public RemoveMember(device: Device, ifEntry: IfEntry = null): void {
        if (this.LinkMembers.Count <= 0)
            return;

        this.LinkMembers.Remove(device, ifEntry);
    }

    public IsLinkedToIp(deviceIpAddress: IpAddress): boolean {
        if (!deviceIpAddress)
            return false;

        return this.LinkMembers.Some((linkMember) => {
            return linkMember.Device && linkMember.Device.ContainsIpAddress(deviceIpAddress);
        });
    }

    public IsLinkedToMac(deviceMacAddress: MacAddress): boolean {
        if (!deviceMacAddress)
            return false;

        return this.LinkMembers.Some((linkMember) => {
            return linkMember.Device && linkMember.Device.MacAddresses.Contains(deviceMacAddress);
        });
    }

    public IsLinkedToDevice(remoteDevice: Device, remoteIfEntry?: IfEntry): boolean {
        if (remoteDevice == null)
            return false;

        return this.LinkMembers.Some((linkMember) => {
            if (linkMember.Device == null)
                return false;

            if (linkMember.Device == remoteDevice) {
                if (!remoteIfEntry)
                    return true;

                if (!linkMember.HasIfEntry)
                    return false;

                if (linkMember.IfEntry.IfIndex == remoteIfEntry.IfIndex)
                    return true;
            }

            return false;
        }, this);
    }

    public get IsApLink(): boolean {
        if (this.LinkMembers.Count <= 1)
            return false;

        var switchMembers: LinkMemberSet = this.LinkMembers.GetSwitchMembers();
        var apMembers: LinkMemberSet = this.LinkMembers.GetWirelessApMembers();

        return ((switchMembers.Count >= 1) && (apMembers.Count >= 1))
    }

    public BuildHistoricalLink(): Link {
        var historyLink: Link = null;

        if (this.LinkMembers.ContainsHistoricalMembers()) {
            var networkLinkMembers: LinkMemberSet = this.LinkMembers.GetNetworkMembers();
            if (networkLinkMembers.Count >= 1) {
                // TODO: If networkLinkMembers >= 2 (with no NonNetworkMembers)

                var nodeLinkMembers: LinkMemberSet = this.LinkMembers.GetNonNetworkMembers();
                if (nodeLinkMembers.Count >= 1) {
                    historyLink = new Link(LinkType.Historical);
                    if (historyLink != null) {
                        historyLink.AddMembers(networkLinkMembers);
                        historyLink.AddMembers(nodeLinkMembers);

                        historyLink.LinkMembers.SetLinkType(LinkType.Historical);
                    }
                }
            }
        }

        return historyLink;
    }

    public IsHistoricalLink(): boolean {
        if (this.LinkType == LinkType.Historical)
            return true;

        return (this.LinkMembers.ContainsHistoricalMembers());
    }

    public IsCoreLink(): boolean {
        var isCoreLink: boolean = null;

        this.LinkMembers.Some((linkMember) => {
            if (!linkMember.HasDevice)
                return;

            if (linkMember.Device.IsCoreDevice())
                isCoreLink = true;
            else {
                isCoreLink = false;
                return true;
            }
        });

        return (isCoreLink);
    }

    public toString() {
        return this.DisplayName;
    }

	//public GetHashCode(): number {
	//	let result = 0;

	//	this.LinkMembers.ForEach((member: LinkMember) => {
	//		result += member.GetHashCode();
	//	}, this);

	//	return result;
 //   }

 //   public Equals(obj: any): boolean {
 //       if (obj == null)
 //           return false;

 //       if (obj === this)
 //           return true;

 //       if (!(obj instanceof Link))
 //           return false;

 //       var other = <Link>obj;
 //       if (this.LinkMembers.Count != other.LinkMembers.Count)
 //           return false;

 //       return this.LinkMembers.Every((linkMember) => {
 //           return other.LinkMembers.Contains(linkMember);
 //       });
 //   }

    //public static FromJson(json: LinkJson): Link {

    //    var linkType: LinkType = LinkType[json.link_type];
    //    var result = new Link(linkType);

    //    if (json.link_members !== undefined) {
    //        json.link_members.forEach((entry) => {
    //            result.LinkMembers.Add(LinkMember.FromJson(entry.link_member));
    //        }, this);
    //    }

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.link = {};
		obj = obj.link;

		obj.link_type = LinkType[this.LinkType];

		obj.link_members = [];
		ObjSerUtils.WriteObjSerCollection(obj.link_members, this.LinkMembers, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.link)
			throw new Error();
		obj = obj.link;

		this.Clear();

		if (obj.link_type !== undefined)
			this.LinkType = LinkType[<string>obj.link_type];

		if (obj.link_members !== undefined)
			ObjSerUtils.ReadObjSerCollection(obj.link_members, this.LinkMembers,
				(value: any) => new LinkMember(), context);
	}
}
